import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Badge, Field, Notice } from '../../components/ui/misc'
import { useToast } from '../../components/ui/toast'
import { useMe } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { authErrorMessage } from '../auth/AuthLayout'
import { Section } from './SettingsLayout'

export function ProfilePage() {
  const { data: me } = useMe()
  const qc = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState(me?.user?.name ?? '')
  const [saving, setSaving] = useState(false)
  const [pw, setPw] = useState({ current: '', next: '' })
  const [pwErr, setPwErr] = useState<string | null>(null)
  const [pwBusy, setPwBusy] = useState(false)
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => (await authClient.listAccounts()).data ?? [],
  })
  const hasPassword = accounts?.some((a) => a.providerId === 'credential')

  const saveName = async () => {
    setSaving(true)
    await authClient.updateUser({ name })
    setSaving(false)
    toast('Profile saved')
    void qc.invalidateQueries({ queryKey: ['me'] })
  }

  const changePassword = async () => {
    setPwBusy(true)
    setPwErr(null)
    const { error } = await authClient.changePassword({ currentPassword: pw.current, newPassword: pw.next, revokeOtherSessions: true })
    setPwBusy(false)
    if (error) return setPwErr(authErrorMessage(error))
    setPw({ current: '', next: '' })
    toast('Password changed; other sessions were signed out')
  }

  return (
    <>
      <Section
        title="Profile"
        footer={
          <Button onClick={saveName} loading={saving} disabled={!name.trim() || name === me?.user?.name}>
            Save
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Email" hint="Contact support to change your email.">
            <input className="input" value={me?.user?.email ?? ''} disabled />
          </Field>
        </div>
      </Section>
      <Section title="Sign-in methods">
        <div className="flex flex-wrap gap-2">
          {(accounts ?? []).map((a) => (
            <Badge key={a.id} tone="neutral">
              {a.providerId === 'credential' ? 'Email and password' : a.providerId === 'google' ? 'Google' : a.providerId}
            </Badge>
          ))}
        </div>
      </Section>
      {hasPassword ? (
        <Section
          title="Password"
          footer={
            <Button onClick={changePassword} loading={pwBusy} disabled={!pw.current || pw.next.length < 10}>
              Change password
            </Button>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Current password">
              <input className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            </Field>
            <Field label="New password" hint="At least 10 characters.">
              <input className="input" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            </Field>
          </div>
          {pwErr && (
            <div className="mt-3">
              <Notice tone="warning">{pwErr}</Notice>
            </div>
          )}
        </Section>
      ) : null}
    </>
  )
}
